import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronRight, MoreVertical } from 'lucide-react';

export interface DropdownItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'info' | 'success' | 'warning';
  className?: string;
  isDivider?: boolean; // New: Support for dividers like in UI Page line 707
  children?: DropdownItem[];
}

interface DropdownMenuProps {
  trigger?: ReactNode;
  title?: string;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

export default function DropdownMenu({ 
  trigger, 
  title, 
  items, 
  align = 'right',
  className = '' 
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveSubmenuIndex(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !menuRef.current || !panelRef.current) return;

    const triggerRect = menuRef.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    if (spaceBelow < panelRect.height + 12 && spaceAbove > spaceBelow) {
      setOpenDirection('up');
    } else {
      setOpenDirection('down');
    }
  }, [isOpen, items.length, title]);

  const getVariantStyles = (variant?: string) => {
    switch (variant) {
      case 'danger': return 'text-rose-600 hover:bg-rose-50';
      case 'success': return 'text-emerald-600 hover:bg-emerald-50';
      case 'info': return 'text-blue-600 hover:bg-blue-50';
      case 'warning': return 'text-amber-600 hover:bg-amber-50';
      default: return 'text-gray-700 hover:bg-gray-50';
    }
  };

  const getIconStyles = (variant?: string) => {
    switch (variant) {
      case 'danger': return 'text-rose-500';
      case 'success': return 'text-emerald-500';
      case 'info': return 'text-blue-500';
      case 'warning': return 'text-amber-500';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className={`relative inline-block ${className}`} ref={menuRef}>
      {/* Trigger Button - Matched with UIPage.tsx line 692 */}
      <div onClick={() => {
        setIsOpen(!isOpen);
        setActiveSubmenuIndex(null);
      }}>
        {trigger || (
          <button
            title="Daha Fazla İşlem"
            className={`flex h-9 w-9 items-center justify-center rounded-lg transition duration-200 shadow-sm ${
              isOpen 
                ? 'bg-gray-100 text-gray-950 ring-2 ring-gray-200' 
                : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 ring-0 ring-transparent active:scale-95'
            }`}
          >
            <MoreVertical size={20} />
          </button>
        )}
      </div>

      {/* Dropdown Panel - Matched with UIPage.tsx line 697 */}
      {isOpen && (
        <div 
          ref={panelRef}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} ${
            openDirection === 'up'
              ? 'bottom-full mb-1.5 origin-bottom-right slide-in-from-bottom-2'
              : 'top-full mt-1.5 origin-top-right slide-in-from-top-2'
          } z-[100] w-48 rounded-lg border border-transparent bg-white py-1.5 shadow-lg ring-1 ring-black/5 animate-in fade-in duration-150`}
        >
          {title && (
            <div className="mb-1 px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">
              {title}
            </div>
          )}
          
          <div className="flex flex-col">
            {items.map((item, index) => {
              if (item.isDivider) {
                // Divider Style - UIPage.tsx line 707
                return (
                  <div key={`div-${index}`} className="my-1.5 h-px bg-gray-100 w-[calc(100%-16px)] mx-auto" />
                );
              }

              return (
                <button
                  key={index}
                  type="button"
                  onMouseEnter={() => setActiveSubmenuIndex(item.children?.length ? index : null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.children?.length) {
                      setActiveSubmenuIndex((prev) => (prev === index ? null : index));
                      return;
                    }
                    item.onClick();
                    setIsOpen(false);
                    setActiveSubmenuIndex(null);
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium transition duration-150 ${getVariantStyles(item.variant)} ${item.className || ''}`}
                >
                  {item.icon && (
                    <span className={`shrink-0 ${getIconStyles(item.variant)}`}>
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.label}</span>
                  {item.children?.length ? (
                    <ChevronRight size={14} className="ml-auto shrink-0 text-gray-400" />
                  ) : null}
                </button>
              );
            })}
          </div>

          {activeSubmenuIndex !== null && items[activeSubmenuIndex]?.children?.length ? (
            <div
              className={`absolute ${align === 'right' ? 'right-full mr-1.5' : 'left-full ml-1.5'} top-0 z-[110] w-52 rounded-lg border border-transparent bg-white py-1.5 shadow-lg ring-1 ring-black/5 animate-in fade-in slide-in-from-top-2 duration-150`}
              onMouseLeave={() => setActiveSubmenuIndex(null)}
            >
              <div className="flex flex-col">
                {items[activeSubmenuIndex].children!.map((child, childIndex) => (
                  <button
                    key={`${activeSubmenuIndex}-${childIndex}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      child.onClick();
                      setIsOpen(false);
                      setActiveSubmenuIndex(null);
                    }}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium transition duration-150 ${getVariantStyles(child.variant)} ${child.className || ''}`}
                  >
                    {child.icon && (
                      <span className={`shrink-0 ${getIconStyles(child.variant)}`}>
                        {child.icon}
                      </span>
                    )}
                    <span className="truncate">{child.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
